import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Modal from './Modal'
import Button from './Button'
import Label from './Label'
import RadioGroup from './RadioGroup'
import DateTimeInput from './DateTimeInput'
import { exportSchema } from '../schemas'

export default function ExportModal({ title, exportUrl, onClose, extraParams = {}, children }) {
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(exportSchema),
    defaultValues: {
      dateRange: '28',
      startDate: '',
      endDate: ''
    }
  })

  const dateRange = watch('dateRange')

  const onSubmit = (data) => {
    const params = new URLSearchParams()
    params.set('date_range', data.dateRange)

    if (data.dateRange === 'custom') {
      if (data.startDate) params.set('start_date', data.startDate)
      if (data.endDate) params.set('end_date', data.endDate)
    }

    Object.entries(extraParams).forEach(([key, value]) => {
      if (value && value !== 'all') params.set(key, value)
    })

    window.location.href = `${exportUrl}?${params.toString()}`
    onClose()
  }

  return (
    <Modal onClose={onClose} title={title}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Date Range */}
        <div>
          <Label className="mb-3">Date Range</Label>
          <RadioGroup
            name="dateRange"
            options={[
              { value: '7', label: 'Last 7 days' },
              { value: '28', label: 'Last 28 days' },
              { value: '90', label: 'Last 90 days' },
              { value: 'custom', label: 'Custom range' }
            ]}
            value={dateRange}
            onChange={(value) => setValue('dateRange', value)}
          />

          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                <DateTimeInput
                  type="date"
                  value={watch('startDate')}
                  onChange={(e) => setValue('startDate', e.target.value, { shouldValidate: true })}
                />
                {errors.startDate && (
                  <p className="mt-1 text-xs text-red-600">{errors.startDate.message}</p>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Date</label>
                <DateTimeInput
                  type="date"
                  value={watch('endDate')}
                  onChange={(e) => setValue('endDate', e.target.value, { shouldValidate: true })}
                />
                {errors.endDate && (
                  <p className="mt-1 text-xs text-red-600">{errors.endDate.message}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {children}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            Export CSV
          </Button>
        </div>
      </form>
    </Modal>
  )
}
